import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Menu, ChevronDown, LogOut, KeyRound, Building2, Check } from "lucide-react";
import { clsx } from "clsx";
import { useAuth } from "../../hooks/useAuth";
import { routeForPages } from "../../hooks/useDefaultRoute";
import { Badge } from "../ui/Badge";
import { NotificationBell } from "../ui/NotificationBell";
import { ChangePasswordModal } from "../../features/auth/ChangePasswordModal";

interface TopBarProps {
  onMenuToggle: () => void;
}

export function TopBar({ onMenuToggle }: TopBarProps) {
  const {
    user,
    signOut,
    isAdmin,
    businesses,
    activeBusinessId,
    setActiveBusiness,
    isBusinessAdmin,
    pagesForBusiness,
  } = useAuth();

  const navigate = useNavigate();

  // Only offer businesses the user can actually access (has at least one page in).
  const switchableBusinesses = businesses.filter((b) => {
    const p = pagesForBusiness(b.businessId);
    return p === "*" || p.length > 0;
  });

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [bizDropdownOpen, setBizDropdownOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const bizDropdownRef = useRef<HTMLDivElement>(null);

  // Close user dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    if (dropdownOpen) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [dropdownOpen]);

  // Close business dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (bizDropdownRef.current && !bizDropdownRef.current.contains(e.target as Node)) {
        setBizDropdownOpen(false);
      }
    };
    if (bizDropdownOpen) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [bizDropdownOpen]);

  const displayName = [user?.firstName, user?.lastName].filter(Boolean).join(" ") || user?.username || "User";
  const initials = user?.firstName && user?.lastName
    ? `${user.firstName[0]}${user.lastName[0]}`.toUpperCase()
    : (user?.username?.[0] ?? "U").toUpperCase();

  const activeBusiness = switchableBusinesses.find((b) => b.businessId === activeBusinessId);
  // Show the switcher to anyone who can access more than one business (employees
  // included — they may hold different roles per business and need to switch context).
  // Admins keep the switcher even with a single business (super-admin sees all).
  const showBizSwitcher =
    switchableBusinesses.length > 1 ||
    ((isAdmin || isBusinessAdmin) && switchableBusinesses.length > 0);

  const handleSignOut = async () => {
    setDropdownOpen(false);
    await signOut();
  };

  const handleChangePassword = () => {
    setDropdownOpen(false);
    setChangePasswordOpen(true);
  };

  return (
    <>
      <header
        className="flex flex-col bg-white border-b border-[#e2e8f0] shrink-0"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <div className="flex items-center justify-between h-14 px-4">
          {/* Left */}
          <div className="flex items-center gap-3">
            {/* Mobile hamburger */}
            <button
              onClick={onMenuToggle}
              className="flex md:hidden items-center justify-center h-8 w-8 rounded-lg text-[#64748b] hover:bg-[#f1f5f9] transition-colors"
              aria-label="Toggle sidebar"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>

          {/* Right */}
          <div className="flex items-center gap-2">
            {/* Business switcher — shown to super-admin and business admins */}
            {showBizSwitcher && (
              <div className="relative" ref={bizDropdownRef}>
                <button
                  onClick={() => setBizDropdownOpen((v) => !v)}
                  className={clsx(
                    "flex items-center gap-1.5 h-8 px-2 sm:px-3 rounded-lg border text-sm font-medium transition-colors",
                    bizDropdownOpen
                      ? "bg-[#f1f5f9] border-[#2447d9] text-[#2447d9]"
                      : "bg-[#f8fafc] border-[#e2e8f0] text-[#374151] hover:border-[#2447d9] hover:text-[#2447d9]",
                  )}
                  aria-haspopup="true"
                  aria-expanded={bizDropdownOpen}
                >
                  <Building2 className="h-4 w-4 shrink-0" />
                  <span className="max-w-[80px] sm:max-w-[140px] truncate text-xs sm:text-sm">
                    {activeBusiness?.name ?? "Select Business"}
                  </span>
                  <ChevronDown
                    className={clsx("h-3.5 w-3.5 shrink-0 transition-transform", bizDropdownOpen && "rotate-180")}
                  />
                </button>

                {bizDropdownOpen && (
                  <div className="absolute right-0 top-full mt-1 w-64 bg-white rounded-xl border border-[#e2e8f0] shadow-[0_4px_16px_0_rgb(0_0_0/0.12)] z-50 py-1">
                    <p className="px-4 py-2 text-[10px] font-semibold uppercase tracking-widest text-[#94a3b8]">
                      Switch Business
                    </p>
                    {switchableBusinesses.map((biz) => (
                      <button
                        key={biz.businessId}
                        onClick={() => {
                          if (biz.businessId !== activeBusinessId) {
                            setActiveBusiness(biz.businessId);
                            // Land on a page the user can actually access in the new
                            // business, so switching never dumps them on /unauthorized.
                            navigate(routeForPages(pagesForBusiness(biz.businessId)));
                          }
                          setBizDropdownOpen(false);
                        }}
                        className="flex w-full items-center justify-between gap-2 px-4 py-2.5 text-sm text-[#0f172a] hover:bg-[#f8fafc] transition-colors"
                      >
                        <div className="flex flex-col items-start text-left">
                          <span className="font-medium">{biz.name}</span>
                          {biz.isBusinessAdmin && (
                            <span className="text-[10px] text-[#2447d9]">Admin</span>
                          )}
                        </div>
                        {biz.businessId === activeBusinessId && (
                          <Check className="h-4 w-4 text-[#2447d9] shrink-0" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Notifications */}
            <NotificationBell />

            {/* User dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen((v) => !v)}
                className={clsx(
                  "flex items-center gap-2 rounded-lg px-2 py-1 transition-colors",
                  dropdownOpen ? "bg-[#f1f5f9]" : "hover:bg-[#f8fafc]",
                )}
                aria-haspopup="true"
                aria-expanded={dropdownOpen}
              >
                {/* Avatar */}
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-[#2447d9] text-white text-xs font-bold">
                  {initials}
                </div>
                <div className="hidden md:flex flex-col items-start">
                  <span className="text-sm font-medium text-[#0f172a] leading-tight">{displayName}</span>
                  <span className="text-[10px] text-[#94a3b8] leading-tight">
                    {isAdmin ? "Super Admin" : isBusinessAdmin ? "Business Admin" : "Employee"}
                  </span>
                </div>
                <ChevronDown
                  className={clsx(
                    "hidden md:block h-4 w-4 text-[#94a3b8] transition-transform",
                    dropdownOpen && "rotate-180",
                  )}
                />
              </button>

              {/* Dropdown menu */}
              {dropdownOpen && (
                <div className="absolute right-0 top-full mt-1 w-56 bg-white rounded-xl border border-[#e2e8f0] shadow-[0_4px_16px_0_rgb(0_0_0/0.12)] z-50 py-1">
                  <div className="px-4 py-3 border-b border-[#e2e8f0]">
                    <p className="text-sm font-semibold text-[#0f172a]">{displayName}</p>
                    <p className="text-xs text-[#94a3b8] mt-0.5">{user?.email ?? user?.username}</p>
                    <div className="mt-1.5">
                      <Badge variant={isAdmin ? "info" : isBusinessAdmin ? "info" : "default"}>
                        {isAdmin ? "Super Admin" : isBusinessAdmin ? "Business Admin" : "Employee"}
                      </Badge>
                    </div>
                  </div>
                  <div className="py-1">
                    <button
                      onClick={handleChangePassword}
                      className="flex w-full items-center gap-2 px-4 py-2 text-sm text-[#0f172a] hover:bg-[#f8fafc] transition-colors"
                    >
                      <KeyRound className="h-4 w-4 text-[#64748b]" />
                      Change Password
                    </button>
                    <button
                      onClick={handleSignOut}
                      className="flex w-full items-center gap-2 px-4 py-2 text-sm text-[#dc2626] hover:bg-[#fef2f2] transition-colors"
                    >
                      <LogOut className="h-4 w-4" />
                      Sign Out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <ChangePasswordModal
        isOpen={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
      />
    </>
  );
}
